import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@kp/react-sdk';
import {
  Colors,
  Typography,
  classNames,
  createClasses,
  pxToRem,
  spacing,
} from '@kp/react-ui';

import { TimePeriodFilter } from '@/components/TimePeriodFilter';
import type { DashboardPeriod } from '@/constants/dashboard-period';
import { useLocaleDateFns } from '@/hooks/useLocaleDateFns';

import { useGreeting } from '../hooks/useGreeting';

import { DateBadge } from './DateBadge';

const SUBTITLE_SEPARATOR = ' • ';
const SUBTITLE_DATE_FORMAT = 'EEEE, d MMMM yyyy';

const classes = createClasses({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing('md'),
    '@media (max-width: 720px)': {
      flexWrap: 'wrap',
    },
  },
  text: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('xs'),
    minWidth: 0,
  },
  headline: {
    margin: 0,
  },
  subtitle: {
    margin: 0,
    color: Colors.Neutral.textWeak,
    overflowWrap: 'anywhere',
  },
  toggle: {
    width: pxToRem(180),
    flexShrink: 0,
    '@media (max-width: 720px)': {
      width: '100%',
    },
  },
});

export interface WelcomeHeaderProps {
  timeSpan: DashboardPeriod;
  onTimeSpanChange: (value: DashboardPeriod) => void;
  tenantName?: string;
  /**
   * Undefined while it loads. At zero neither the count nor the period
   * toggle is shown, as there is nothing to scope.
   */
  buildingCount?: number;
  date?: Date;
  className?: string;
}

export const WelcomeHeader: React.FC<WelcomeHeaderProps> = ({
  timeSpan,
  onTimeSpanChange,
  tenantName,
  buildingCount,
  date = new Date(),
  className,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const greeting = useGreeting();
  const { format } = useLocaleDateFns();

  const hasNoBuildings = buildingCount === 0;

  const subtitle = [
    format(date, SUBTITLE_DATE_FORMAT),
    tenantName,
    !!buildingCount &&
      t('buildingSelector.allBuildings', { count: buildingCount }),
  ]
    .filter(Boolean)
    .join(SUBTITLE_SEPARATOR);

  return (
    <div className={classNames(classes.root, className)}>
      <DateBadge date={date} />
      <div className={classes.text}>
        <Typography
          variant="headlineLarge"
          component="h1"
          className={classes.headline}
        >
          {greeting}
          {user?.info?.name && `, ${user.info.name}`}
        </Typography>
        <Typography
          variant="labelLarge"
          component="p"
          className={classes.subtitle}
        >
          {subtitle}
        </Typography>
      </div>
      {!hasNoBuildings && (
        <TimePeriodFilter
          value={timeSpan}
          onChange={onTimeSpanChange}
          className={classes.toggle}
        />
      )}
    </div>
  );
};
